import React from 'react';

import { FileModel, FolderModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { useNonce } from '../../../hooks/use-nonce';

export interface FolderListViewContext {
  setCurrentSubFolders: (folders: FolderModel[]) => void;
  setCurrentFolderFiles: (files: FileModel[]) => void;
  setCurrentFolderId: (id?: string) => void;
  setCheckedItems: (folders: FolderModel[], files: FileModel[]) => void;
  setSelectedFile: (file?: FileModel) => void;
  toggleSelectedFile: (file: FileModel) => void;
  setActionBarEnabled: (enabled: boolean) => void;
  currentSubFolders: FolderModel[];
  currentFolderFiles: FileModel[];
  checkedFolders: FolderModel[];
  checkedFiles: FileModel[];
  selectedFile?: FileModel;
  actionBarEnabled: boolean;
  foldersCheckable: boolean;
  filesCheckable: boolean;
  maxFileSelection?: number;
  refresh: () => void;
  refreshNonce: number;
}

export const FolderListViewContext = React.createContext<FolderListViewContext>({
  setCurrentSubFolders: (folders: FolderModel[]) => {
    /* No default implementation */
  },
  setCurrentFolderFiles: (files: FileModel[]) => {
    /* No default implementation */
  },
  setCurrentFolderId: (id?: string) => {
    /* No default implementation */
  },
  setCheckedItems: (folders: FolderModel[], files: FileModel[]) => {
    /* No default implementation */
  },
  setSelectedFile: (file?: FileModel) => {
    /* No default implementation */
  },
  toggleSelectedFile: (file: FileModel) => {
    /* No default implementation */
  },
  setActionBarEnabled: (ebaled: boolean) => {
    /* No default implementation */
  },
  currentSubFolders: [],
  currentFolderFiles: [],
  checkedFolders: [],
  checkedFiles: [],
  selectedFile: undefined,
  actionBarEnabled: false,
  foldersCheckable: true,
  filesCheckable: true,
  maxFileSelection: undefined,
  refresh: () => {
    /* No default implementation */
  },
  refreshNonce: 0,
});

type Props = {
  children: React.ReactNode;
  foldersCheckable: boolean;
  filesCheckable: boolean;
  onCheckedFilesChanged?: (files: FileModel[]) => void;
  onCheckedFoldersChanged?: (folder: FolderModel[]) => void;
  onCurrentFolderIdChanged?: (id?: string) => void;
  maxFileSelection?: number;
  maxFolderSelection?: number;
};

//
// todo: Change checkedFolders, checkedFiles, selectedFile to be string[] (id) instead of complete
// todo: model, and instead pick model from currentSubFolders/currentFolderFiles when model is needed.
//
const FolderListViewState = (props: Props) => {
  const [currentFolderFiles, setCurrentFolderFiles] = React.useState<FileModel[]>([]);
  const [currentSubFolders, setCurrentSubFolders] = React.useState<FolderModel[]>([]);
  const [currentFolderId, setCurrentFolderId] = React.useState<string>();
  const [checkedFolders, setCheckedFolders] = React.useState<FolderModel[]>([]);
  const [checkedFiles, setCheckedFiles] = React.useState<FileModel[]>([]);
  const [selectedFile, setSelectedFile] = React.useState<FileModel>();
  const [actionBarEnabled, setActionBarEnabled] = React.useState(false);
  const [refreshNonce, refresh] = useNonce();

  React.useEffect(() => {
    // When currentFolderFiles changes, check whether the selected file does not exist
    // in this list (file was deleted). Then update state to not mark this file as selected anymore.
    if (selectedFile && !currentFolderFiles.find((x) => x.id === selectedFile.id)) {
      setSelectedFile(undefined);
    }

    // When currentFolderFiles changes, update checkedFiles and keep only
    // current checked folders that also exist in currentFolderFiles.
    const newCheckedFiles = currentFolderFiles.filter((x) =>
      checkedFiles.some((y) => y.id === x.id),
    );
    if (checkedFiles.length !== newCheckedFiles.length) {
      setCheckedFiles(newCheckedFiles);
    }
  }, [checkedFiles, currentFolderFiles, selectedFile]);

  React.useEffect(() => {
    // When currentSubFolders changes, update checkedFolders and keep only
    // current checked folders that also exist in currentSubFolders.
    const newCheckedFolders = currentSubFolders.filter((x) =>
      checkedFolders.some((y) => y.id === x.id),
    );
    if (checkedFolders.length !== newCheckedFolders.length) {
      setCheckedFolders(newCheckedFolders);
    }
  }, [checkedFolders, currentSubFolders]);

  const setCheckedItems = React.useCallback((folders: FolderModel[], files: FileModel[]) => {
    setCheckedFolders(folders);
    setCheckedFiles(files);
  }, []);

  React.useEffect(() => {
    props.onCheckedFilesChanged?.(checkedFiles);
  }, [checkedFiles, props]);

  React.useEffect(() => {
    props.onCheckedFilesChanged?.(checkedFiles);
  }, [checkedFiles, props]);

  React.useEffect(() => {
    props.onCurrentFolderIdChanged?.(currentFolderId);
  }, [currentFolderId, props]);

  const toggleSelectedFile = React.useCallback((file: FileModel) => {
    setSelectedFile((x) => (file.id === x?.id ? undefined : file));
  }, []);

  return (
    <FolderListViewContext.Provider
      value={{
        setCurrentSubFolders,
        setCurrentFolderFiles,
        setCurrentFolderId,
        setCheckedItems,
        setSelectedFile,
        toggleSelectedFile,
        setActionBarEnabled,
        currentSubFolders,
        currentFolderFiles,
        checkedFolders,
        checkedFiles,
        selectedFile,
        actionBarEnabled,
        foldersCheckable: props.foldersCheckable,
        filesCheckable: props.filesCheckable,
        maxFileSelection: props.maxFileSelection,
        refresh,
        refreshNonce,
      }}
    >
      {props.children}
    </FolderListViewContext.Provider>
  );
};

export { FolderListViewState };
